
import { Page, Text, View, Document, StyleSheet, Image } from "@react-pdf/renderer";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

const styles = StyleSheet.create({
  page: {
    padding: 40,
    backgroundColor: "#FFFFFF",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 40,
  },
  headerLeft: {
    flexDirection: "column",
  },
  headerRight: {
    flexDirection: "column",
    alignItems: "flex-end",
  },
  logo: {
    width: 45, 
    marginBottom: 15,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#1f2937",
    marginBottom: 4,
  },
  invoiceNumber: {
    fontSize: 12,
    color: "#4b5563",
  },
  detailsSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 40,
  },
  detailsColumn: {
    width: "48%",
  },
  detailsTitle: {
    fontSize: 10,
    fontWeight: "medium",
    color: "#4b5563",
    marginBottom: 12,
  },
  detailsContent: {
    fontSize: 9,
    color: "#1f2937",
    marginBottom: 6,
  },
  detailsBold: {
    fontSize: 9,
    fontWeight: "bold",
    marginBottom: 8,
  },
  table: {
    flexDirection: "column",
    marginBottom: 40,
  },
  tableHeader: {
    flexDirection: "row",
    borderBottomWidth: 2,
    borderBottomColor: "#d1d5db",
    paddingBottom: 8,
    marginBottom: 8,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
    paddingVertical: 8,
  },
  totalRow: {
    flexDirection: "row",
    borderBottomWidth: 2,
    borderBottomColor: "#000000",
    paddingVertical: 12,
  },
  descriptionCell: {
    flex: 2,
    paddingHorizontal: 8,
    fontSize: 8,
  },
  totalDescriptionCell: {
    flex: 2,
    paddingHorizontal: 8,
    fontSize: 10,
    fontWeight: "bold",
  },
  cell: {
    flex: 1,
    textAlign: "right",
    paddingHorizontal: 8,
    fontSize: 8,
  },
  totalCell: {
    flex: 1,
    textAlign: "right",
    paddingHorizontal: 8,
    fontSize: 10,
    fontWeight: "bold",
  },
  headerCell: {
    flex: 1,
    textAlign: "right",
    paddingHorizontal: 8,
    fontSize: 9,
  },
  headerDescriptionCell: {
    flex: 2,
    paddingHorizontal: 8,
    fontSize: 9,
  },
  bankDetails: {
    marginTop: 20,
  },
  bankDetailsTitle: {
    fontSize: 12,
    fontWeight: "medium",
    color: "#4b5563",
    marginBottom: 12,
  },
  bankDetailsContent: {
    fontSize: 10,
    color: "#1f2937",
    marginBottom: 8,
  },
});

export type InvoiceData = {
  invoiceNumber: string;
  issueDate: Date;
  dueDate: Date;
  businessName: string;
  businessTaxId: string;
  businessIban: string;
  businessSwift: string;
  businessEmail: string;
  businessPhone: string;
  recipientName: string;
  recipientEmail: string;
  recipientAddress: string;
  clientTaxId: string;
  clientPhone: string;
  description: string;
  quantity: number;
  unitPrice: number;
  bankAccountHolder: string;
  bankIban: string;
  bankName: string;
  bankSwift: string;
  paymentReference: string;
  taxes: Array<{ name: string; percentage: number }>;
  logoUrl?: string; // Add optional logo URL
};

export const InvoicePDF = ({ data }: { data: InvoiceData }) => {
  const subtotal = data.quantity * data.unitPrice;
  const totalTaxAmount = data.taxes.reduce((total, tax) => {
    return total + (subtotal * tax.percentage / 100);
  }, 0);
  const total = subtotal + totalTaxAmount;
  const { t } = useTranslation();


  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            {data.logoUrl && (
              <Image
                src={data.logoUrl}
                style={styles.logo}
              />
            )}
            <Text style={styles.title}>{t("invoice_pdf.capital_invoice", "INVOICE")}</Text>
            <Text style={styles.invoiceNumber}>#{data.invoiceNumber}</Text>
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.detailsTitle}>{t("invoice_pdf.date", "Date")}</Text>
            <Text style={styles.detailsContent}>{format(data.issueDate, "PP")}</Text>
            <Text style={styles.detailsTitle}>{t("invoice_pdf.due_date", "Due Date")}</Text>
            <Text style={styles.detailsContent}>{format(data.dueDate, "PP")}</Text>
          </View>
        </View>

        {/* Company and Client Details */}
        <View style={styles.detailsSection}>
          <View style={styles.detailsColumn}>
            <Text style={styles.detailsTitle}>{t("invoice_pdf.from", "From")}</Text>
            <Text style={styles.detailsBold}>{data.businessName}</Text>
            <Text style={styles.detailsContent}>{t("invoice_pdf.tax_id", "Tax ID: ")} {data.businessTaxId}</Text>
            <Text style={styles.detailsContent}>{t("invoice_pdf.email", "Email: ")} {data.businessEmail}</Text>
            <Text style={styles.detailsContent}>{t("invoice_pdf.phone", "Phone: ")} {data.businessPhone}</Text>
            <Text style={styles.detailsContent}>{t("invoice_pdf.iban", "IBAN: ")} {data.businessIban}</Text>
            <Text style={styles.detailsContent}>{t("invoice_pdf.swift", "SWIFT: ")} {data.businessSwift}</Text>
          </View>
          <View style={styles.detailsColumn}>
            <Text style={styles.detailsTitle}>{t("invoice_pdf.bill_to", "Bill To")}</Text>
            <Text style={styles.detailsBold}>{data.recipientName}</Text>
            <Text style={styles.detailsContent}>{data.recipientAddress}</Text>
            <Text style={styles.detailsContent}>{t("invoice_pdf.tax_id", "Tax ID: ")} {data.clientTaxId}</Text>
            <Text style={styles.detailsContent}>{t("invoice_pdf.email", "Email: ")} {data.recipientEmail}</Text>
            <Text style={styles.detailsContent}>{t("invoice_pdf.phone", "Phone: ")} {data.clientPhone}</Text>
          </View>
        </View>

        {/* Items Table */}
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={styles.headerDescriptionCell}>{t("invoice_pdf.description", "Description")}</Text>
            <Text style={styles.headerCell}>{t("invoice_pdf.quantity", "Quantity")}</Text>
            <Text style={styles.headerCell}>{t("invoice_pdf.rate", "Rate")}</Text>
            <Text style={styles.headerCell}>{t("invoice_pdf.amount", "Amount")}</Text>
          </View>
          
          {/* Line Item */}
          <View style={styles.tableRow}>
            <Text style={styles.descriptionCell}>{data.description}</Text>
            <Text style={styles.cell}>{data.quantity}</Text>
            <Text style={styles.cell}>€ {data.unitPrice.toFixed(2)}</Text>
            <Text style={styles.cell}>€ {subtotal.toFixed(2)}</Text>
          </View>

          {/* Tax Items */}
          {data.taxes.map((tax, index) => {
            const taxAmount = subtotal * tax.percentage / 100;
            return (
              <View key={index} style={[styles.tableRow, { borderTopWidth: 1, borderTopColor: '#e5e7eb' }]}>
                <Text style={styles.descriptionCell}>{tax.name}</Text>
                <Text style={styles.cell}></Text>
                <Text style={styles.cell}>{tax.percentage}%</Text>
                <Text style={styles.cell}>€ {taxAmount.toFixed(2)}</Text>
              </View>
            );
          })}

          {/* Total Row */}
          <View style={styles.totalRow}>
            <Text style={styles.totalDescriptionCell}>{t("invoice_pdf.total", "Total")}</Text>
            <Text style={styles.totalCell}></Text>
            <Text style={styles.totalCell}></Text>
            <Text style={styles.totalCell}>€ {total.toFixed(2)}</Text>
          </View>
        </View>

        {/* Bank Details */}
        <View style={styles.bankDetails}>
          <Text style={styles.bankDetailsTitle}>{t("invoice_pdf.payment_details", "Payment Details")}</Text>
          <Text style={styles.bankDetailsContent}>{t("invoice_pdf.accout_holder", "Account Holder: ")} {data.bankAccountHolder}</Text>
          <Text style={styles.bankDetailsContent}>{t("invoice_pdf.bank_name", "Bank Name: ")} {data.bankName}</Text>
          <Text style={styles.bankDetailsContent}>{t("invoice_pdf.iban", "IBAN: ")} {data.bankIban}</Text>
          <Text style={styles.bankDetailsContent}>{t("invoice_pdf.swift", "SWIFT: ")} {data.bankSwift}</Text>
          <Text style={styles.bankDetailsContent}>{t("invoice_pdf.reference", "Reference: ")} {data.paymentReference}</Text>
        </View>
      </Page>
    </Document>
  );
};
